import { useCallback, useEffect, useRef, useState } from "react";
import { zim } from "../../../../zegoConfig";
import { loginToZego } from "../../../../components/utils/zegoLogin";
import { TeleconsultationData } from "../types";

interface UseZegoChatReturn {
  isLoggedIn: boolean;
  userId: string;
  userDokter: string;
  groupId: string | null;
}

/**
 * Manages ZIM authentication and group joining.
 *
 * IMPORTANT: We intentionally do NOT call leaveGroup on unmount.
 * The user is added to the group from the backend when the consultation is created.
 * Leaving the group would cause missed messages when the user navigates away and returns.
 * The user should remain a group member for the full duration of the consultation.
 */
export function useZegoChat(
  teleconsultationData: TeleconsultationData | null,
): UseZegoChatReturn {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const loginStartedRef = useRef(false);
  const hasJoinedGroupRef = useRef(false);

  // ─── Derived identifiers ──────────────────────────────────────────────────

  const appEnv = process.env.REACT_APP_ZEGOCLOUD_APP_ENV || "";

  const userId = teleconsultationData?.patient_id
    ? `LMS${teleconsultationData.patient_id}${appEnv}`
    : "";

  const username = teleconsultationData?.patient_name || "defaultUser";

  const userDokter = teleconsultationData?.practitioner_id
    ? `LMS${teleconsultationData.practitioner_id}${appEnv}`
    : "";

  const groupId = teleconsultationData?.room_id
    ? String(teleconsultationData.room_id).trim()
    : null;

  // ─── Join group ───────────────────────────────────────────────────────────

  const joinGroup = useCallback(
    async (gId: string) => {
      if (!gId) {
        console.error("❌ groupId kosong");
        return;
      }

      console.log("👥 Mencoba joinGroup:", gId);

      try {
        await zim.joinGroup(gId);
        console.log("✅ Berhasil joinGroup:", gId);
      } catch (error: any) {
        console.warn("⚠️ joinGroup error detail:", JSON.stringify(error));

        const errorCode =
          error?.code ?? error?.errorCode ?? error?.error?.code ?? 0;
        const errorMessage = error?.message ?? error?.error?.message ?? "";

        // Sudah member → anggap sukses, ini kondisi normal
        if (
          errorCode === 6000522 ||
          errorCode === 108037 ||
          errorMessage?.includes("already belong")
        ) {
          console.log("✅ Sudah member group, lanjut:", gId);
          return;
        }

        // Group belum ada → buat baru
        if (errorCode === 660500002 || errorCode === 6000404) {
          try {
            console.log("🔄 Group tidak ada, mencoba createGroup:", gId);
            await zim.createGroup({ groupID: gId, groupName: gId }, [
              userDokter,
            ]);
            console.log("✅ Berhasil createGroup:", gId);
          } catch (createError: any) {
            const createCode = createError?.code ?? createError?.errorCode ?? 0;
            const createMsg = createError?.message ?? "";
            if (
              createCode === 660500001 ||
              createMsg?.includes("already belong")
            ) {
              console.log("✅ Group sudah ada (race condition), lanjut:", gId);
            } else {
              console.error("❌ Gagal createGroup:", createCode, createMsg);
            }
          }
        } else {
          console.error(
            "❌ Gagal joinGroup (unhandled):",
            errorCode,
            errorMessage,
          );
        }
      }
    },
    [userDokter],
  );

  // ─── Login ZIM ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!userId || !username) return;
    if (loginStartedRef.current) return;
    loginStartedRef.current = true;

    const loginZIM = async () => {
      console.log("🔑 Login ZIM untuk userId:", userId);
      const success = await loginToZego(userId, username);
      if (success) {
        setIsLoggedIn(true);
      }
    };

    loginZIM();
  }, [userId, username]);

  // ─── Join group setelah login ─────────────────────────────────────────────

  useEffect(() => {
    if (!isLoggedIn || !groupId || hasJoinedGroupRef.current) return;
    hasJoinedGroupRef.current = true;
    joinGroup(groupId);
  }, [isLoggedIn, groupId, joinGroup]);

  // ─── TIDAK ada leaveGroup saat unmount ────────────────────────────────────
  // User tetap sebagai member group selama sesi konsultasi berlangsung.
  // Jika di-leave, pesan dokter tidak akan masuk saat user di luar halaman chat.
  // Backend yang menghandle penambahan & penghapusan member group.

  return { isLoggedIn, userId, userDokter, groupId };
}
